import React, { useState, useEffect, useRef } from "react";
import { useAudioRecorder } from "react-audio-voice-recorder";
import fixWebmDuration from "webm-duration-fix";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { Button, SecondaryButton, toast } from "@jammable/ui-core";
import { Spinner } from "@jammable/ui-components/Loader";
import { FaStopCircle, FaPlay, FaPause, FaCheck } from "react-icons/fa";
import { IoIosMicrophone } from "react-icons/io";
import styles from "../../styles/Conversion.module.css"; // Ensure path is correct
import { useModal } from "@/context/ModalContext";
import UpgradeModal from "./UpgradeModal";

const MicrophoneRecordView = ({ onSongSelected, onError }) => {
  // --- Microphone State ---
  const [micLoading, setMicLoading] = useState(false); // Loading specific to mic upload
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null); // URL for previewing recording
  const [recordingBlobForUpload, setRecordingBlobForUpload] = useState(null); // The blob to be uploaded
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [recordedDuration, setRecordedDuration] = useState(0); // Track the duration of completed recording
  const audioRef = useRef(null);

  const { openModal } = useModal();

  const {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording: isMicRecording,
    isPaused: isMicPaused,
    recordingTime: micRecordingTime,
  } = useAudioRecorder({
    // Add error handling if needed, e.g., onNotAllowedOrFound
    // onNotAllowedOrFound: (error) => {
    //   console.error("Microphone access error:", error);
    //   toast.error("Microphone access denied or not found.");
    //   onError?.("Microphone access denied or not found.", error);
    // }
  });
  // --- End Microphone State ---

  // --- Audio Playback Ref Cleanup ---
  useEffect(() => {
    // Cleanup function runs on component unmount
    return () => {
      audioRef.current?.pause(); // Stop playback
      if (audioRef.current) {
        audioRef.current.onended = null; // Remove listener
      }
      audioRef.current = null;
      // URL revocation is handled in the other effect's cleanup
    };
  }, []);

  // --- Microphone Effects ---
  // Handle microphone recording blob when it's ready
  useEffect(() => {
    if (!recordingBlob || micLoading) return;

    let currentAudioPreviewUrl = null; // Store the URL locally for cleanup

    const processBlob = async () => {
      try {
        const fixedBlob = await fixWebmDuration(recordingBlob);
        setRecordingBlobForUpload(fixedBlob);
        const url = URL.createObjectURL(fixedBlob);
        currentAudioPreviewUrl = url; // Assign to local variable
        setAudioPreviewUrl(url);
      } catch (error) {
        console.error("Error fixing webm duration or creating URL:", error);
        setRecordingBlobForUpload(recordingBlob); // Fallback to original blob
        const url = URL.createObjectURL(recordingBlob);
        currentAudioPreviewUrl = url; // Assign fallback URL
        setAudioPreviewUrl(url);
        onError?.("Could not process recording.", error);
      }
    };

    processBlob();

    // Cleanup function for the effect using the local variable
    return () => {
      if (currentAudioPreviewUrl) {
        // Clean up the audio element as well when blob changes
        audioRef.current?.pause();
        if (audioRef.current) {
          audioRef.current.onended = null;
        }
        audioRef.current = null;
        setIsPreviewPlaying(false);
        URL.revokeObjectURL(currentAudioPreviewUrl);
      }
    };
  }, [recordingBlob, micLoading, onError]);

  // Effect to setup Audio object when URL is ready
  useEffect(() => {
    if (audioPreviewUrl) {
      audioRef.current = new Audio(audioPreviewUrl);
      audioRef.current.onended = () => {
        setIsPreviewPlaying(false);
      };
      // Get and set the duration of the recorded audio
      audioRef.current.onloadedmetadata = () => {
        setRecordedDuration(Math.round(audioRef.current.duration));
      };
    } else {
      // Ensure cleanup if URL becomes null
      audioRef.current?.pause();
      if (audioRef.current) {
        audioRef.current.onended = null;
      }
      audioRef.current = null;
      setIsPreviewPlaying(false);
      setRecordedDuration(0);
    }
  }, [audioPreviewUrl]);
  // --- End Microphone Effects ---

  // --- Microphone Upload Logic ---
  const handleMicUpload = async () => {
    if (!recordingBlobForUpload || micLoading) return;

    setMicLoading(true);

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error("User not authenticated.");
      }
      const token = await auth.currentUser.getIdToken();

      const presignedResponse = await axios
        .get("/api/s3/modelS3PresignedUrl", {
          headers: { Authorization: token },
        })
        .catch((err) => {
          throw new Error(
            err.response?.data?.message || "Failed to prepare upload location.",
          );
        });

      const { url: uploadUrl, absoluteUrl } = presignedResponse.data;

      await axios
        .put(uploadUrl, recordingBlobForUpload, {
          headers: {
            "Content-Type": recordingBlobForUpload.type || "audio/webm",
          },
        })
        .catch((err) => {
          throw new Error(
            err.response?.data?.message || "Failed to upload file.",
          );
        });

      const fileName = `Recording_${new Date().toISOString()}.webm`;
      onSongSelected({
        url: absoluteUrl,
        title: fileName,
        metadata: { source: "microphone" },
      });

      // Reset state after successful selection/upload is handled by parent unmounting this view
      // setMicLoading(false); // Already handled by parent
    } catch (err) {
      console.error("Microphone upload failed:", err);
      setMicLoading(false);
      const message = err.message || "Microphone upload failed.";

      if (message === "You are out of credits") {
        openModal(
          "upgrade-modal",
          <UpgradeModal
            from="microphone-record-view"
            dailyLimit={false}
            message="You are out of credits"
          />,
          { className: "tw-p-0" },
        );
      } else {
        toast.error(message);
      }
      onError?.(message, err);
    }

    // Stop playback if it was playing before upload
    audioRef.current?.pause();
    setIsPreviewPlaying(false);
  };

  const handleStopAndClearMic = () => {
    if (isMicRecording) {
      stopRecording();
    }
    // Also stop and clean up audio playback state
    audioRef.current?.pause();
    setIsPreviewPlaying(false);
    setRecordingBlobForUpload(null);
    setAudioPreviewUrl(null); // This will trigger the cleanup effect
    setMicLoading(false); // Ensure loading state is reset if clearing
  };

  // --- Audio Playback Control ---
  const togglePreviewPlayback = () => {
    if (!audioRef.current) return;

    if (isPreviewPlaying) {
      audioRef.current.pause();
      setIsPreviewPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => {
          setIsPreviewPlaying(true);
        })
        .catch((err) => {
          console.error("Error playing audio preview:", err);
          toast.error("Could not play audio preview.");
          setIsPreviewPlaying(false); // Reset state on error
        });
    }
  };
  // --- End Audio Playback Control ---

  const formatSeconds = (s) => {
    const minutes = Math.floor(s / 60);
    const seconds = s % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };
  // --- End Microphone Upload Logic ---

  return (
    // --- Microphone Recording UI ---
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
      <div
        className={`tw-w-60 tw-relative tw-h-60 tw-flex tw-items-center tw-justify-center tw-rounded-full ${isMicRecording ? `tw-border-2 tw-border-solid ${!isMicPaused ? styles["animate-ring2"] : "tw-border-purple-400"}` : ""}`}
      >
        <div
          className={`tw-w-48 tw-h-48 tw-flex tw-items-center tw-justify-center tw-rounded-full ${isMicRecording ? `tw-border-2 tw-border-solid ${!isMicPaused ? styles["animate-ring3"] : "tw-border-purple-700"}` : ""}`}
        >
          <div
            onClick={() => {
              if (isMicRecording) {
                stopRecording();
              } else {
                handleStopAndClearMic(); // Clear previous state if any
                startRecording();
              }
            }}
            className={`tw-w-40 tw-h-40 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-relative tw-cursor-pointer tw-transition-all tw-duration-150 tw-border-b-8 active:tw-translate-y-1 active:tw-border-b-4 active:tw-scale-[0.98] ${
              isMicRecording
                ? "tw-bg-red-500 hover:tw-bg-red-600 tw-border-red-700"
                : "tw-bg-purple-700 hover:tw-bg-purple-800 tw-border-purple-900"
            }`}
          >
            {isMicRecording ? (
              <FaStopCircle className="tw-text-white" size={60} />
            ) : (
              <IoIosMicrophone className="tw-text-white" size={100} />
            )}
          </div>
        </div>
        {!isMicRecording && !recordingBlobForUpload && (
          <div className="tw-text-lg tw-absolute tw-bottom-0 tw-text-black/40  tw-uppercase tw-font-black tw-tracking-widest">
            Click to record
          </div>
        )}
      </div>{" "}
      {/* Timer */}
      <div className="tw-bg-gray-100 tw-p-5 tw-text-purple-700 tw-font-bold tw-rounded-xl tw-py-2 tw-flex tw-items-center tw-gap-2">
        {recordingBlobForUpload
          ? formatSeconds(recordedDuration)
          : formatSeconds(micRecordingTime)}
        {recordingBlobForUpload && !isMicRecording && (
          <FaCheck className="tw-text-green-500" />
        )}
      </div>
      {/* No separate preview player, integrated into buttons below */}
      {/* Loading Indicator */}
      {micLoading &&
        !audioPreviewUrl && ( // Show general loading only before preview is ready
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-mt-4">
            <Spinner className="tw-border-purple-700" size={6} />
            <span className="tw-text-sm tw-text-gray-500">Processing...</span>
          </div>
        )}
      {/* Action Button Area */}
      <div className="tw-flex tw-gap-2 tw-items-center tw-justify-center tw-w-full">
        {audioPreviewUrl && (
          <>
            {/* Play/Pause Button */}

            {/* Continue/Upload Button */}
            <Button
              onClick={handleMicUpload}
              className="tw-rounded-2xl tw-w-full tw-font-black tw-uppercase tw-text-base tw-h-12 tw-border tw-border-solid tw-border-purple-700 tw-border-b-4 tw-border-b-purple-900 active:tw-translate-y-0.5 active:tw-border-b-2 active:tw-scale-[0.98] tw-transition tw-duration-100 tw-ease-in-out disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              // Disable if no blob, loading, or actively recording (shouldn't happen here but safe)
              disabled={!recordingBlobForUpload || micLoading || isMicRecording}
            >
              {micLoading ? (
                <Spinner className="tw-border-white tw-border-3" size={4} />
              ) : (
                "Continue"
              )}
            </Button>
            <SecondaryButton
              variant="secondary"
              onClick={togglePreviewPlayback}
              className="tw-rounded-2xl tw-flex-shrink-0 tw-p-3" // Adjust padding maybe
              disabled={micLoading} // Disable while uploading
              aria-label={isPreviewPlaying ? "Pause preview" : "Play preview"}
            >
              {isPreviewPlaying ? (
                <FaPause className="tw-w-5 tw-h-5" />
              ) : (
                <FaPlay className="tw-w-5 tw-h-5" />
              )}
            </SecondaryButton>
          </>
        )}
      </div>
    </div>
    // --- End Microphone Recording UI ---
  );
};

export default MicrophoneRecordView;
